// @flow
import * as React from 'react';
import { Link, LinkProps, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs, Button, CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  Tab as MuiTab,
  TabProps,
  Tabs,
  Typography
} from '@mui/material';
import { getRoute } from '../../app/router';
import { getUserName } from '@coverright/utils';
import {
  useClientSubscription,
  ClientCalendlyAppointmentsDocument,
  TabLabel, useAgent,
  UserAvatar
} from '../../shared';
import {
  CalendarCheck,
  CurrencyCircleDollar,
  Envelope,
  FilesIcon, KeyboardIcon,
  Layout, ListChecks, NotePencil,
  PhoneCall, Pulse,
  User, UserSound
} from '@phosphor-icons/react';
import { useState } from 'react';
import { ClientContext } from './hooks/client-context';
import { ClientPersonal } from './ui/client-personal';
import { environment } from '../../../environments/environment';
import { ClientMenu } from './ui/client-menu';
import { useCreateMedicareQuoteIfNotExists } from './hooks/use-create-medicare-quote-if-not-exists';
import { Preloader, ScheduleACallButton } from '@coverright/features';
import { UserInput } from './ui/user-input';
import { Files } from './ui/files';
import { ExpiredContactDateDialog } from './ui/expired-contact-date-dialog';
import { useClientNextActions } from './hooks/use-client-next-actions';
import { enrollmentApolloClient } from '@coverright/data-access/apollo-clients';
import { Tooltip } from '@coverright/ui/inputs';
import { TestCallButton } from './ui/test-call-button';
import { AssignedToOtherAgentNotification } from './ui/assigned-to-other-agent-notification';
import { getIsCSR, keycloak } from '@coverright/shared/keycloak';
import { AgentNotLicensedInClientStateNotification } from './ui/agent-not-licensed-in-client-state-notification';
import { ContactInfo } from './ui/client-personal/contact-info';
import { SoaStatus } from './ui/soa-status';
import { ClientInDncListNotification } from './ui/client-in-dnc-list-notification';


export const Client = () => {
  const {id} = useParams<{ id: string }>();
  const [sideTab, setSideTab] = useState(0);
  const {client, loading} = useClientSubscription(id);
  // const {count: enrollmentsCount} = useEnrollments(client);
  const nextActions = useClientNextActions(id)
  const location = useLocation();
  useCreateMedicareQuoteIfNotExists(client);

  const isCSR = getIsCSR(keycloak)

  const agent = useAgent();

  const tab = location.pathname.split('/').at(-1);
  const mainTab = tab === id ? 'overview' : tab;

  const soaDone = client?.recordedLineAgreementCheck && client?.soaAgreementCheck;
  const allDone = soaDone && client?.tpmoAgreementCheck;
  const soaColor = allDone ? '#03986b' : soaDone ? '#0288d1' : '#a31c1e';

  const callsCount = client.calls?.length || 0;

  const onScheduleClose = () => {
    setTimeout(() => enrollmentApolloClient.refetchQueries({
      include: [ClientCalendlyAppointmentsDocument]
    }), 2000)
  };

  const quoteButtonDisabled = !client?.quoteId || !client?.zip || !client?.countyName;

  return (
    <>
      {client && <ExpiredContactDateDialog client={client} />}
      <AssignedToOtherAgentNotification clientId={id} />
      {client.isDNC && <ClientInDncListNotification />}
      <Box sx={{p: 3, pb: 10}}>
        <Stack gap={2} direction={'row'} alignItems={'center'}>
          <Stack gap={3} direction={'row'} alignItems={'center'} sx={{flex: 1}}>
            <Breadcrumbs aria-label="breadcrumb">
              <MuiLink component={Link}
                       underline="hover" color="inherit"
                       to={`/${getRoute('clients')}`}>
                Clients
              </MuiLink>
              {!client ? <Typography>Loading...</Typography> :
                <Stack gap={2} direction={'row'} alignItems={'center'}>
                  {client && <UserAvatar name={getUserName(client)} email={client.email || ''} size={30} fontSize={12} />}
                  {loading && <CircularProgress size={15} />}
                </Stack>}
            </Breadcrumbs>
            <AgentNotLicensedInClientStateNotification clientState={client?.state} />
          </Stack>

          {!environment.production && <TestCallButton clientId={id} />}

          <div>
            <ScheduleACallButton
              variant={'outlined'}
              size={'small'}
              startIcon={<CalendarCheck />}
              fullWidth
              onClose={onScheduleClose}
              calendlyProps={{
                dataUrl: environment.production && isCSR ? 'https://calendly.com/coverright-advisors/planning' : undefined,
                agent,
                client
            }}
            />
          </div>

          <Tooltip title={quoteButtonDisabled ? 'You have to fill zip and county name for quoting' : ''}>
            <div>
              <Button href={`${environment.quotingUrl}?clientId=${client?.id}`}
                      target={'_blank'}
                      size={'small'}
                      disabled={quoteButtonDisabled}
                      sx={{width: 110}}
                      variant={'contained'}
                      color={'primary'}>Open quote</Button>
            </div>
          </Tooltip>

          <ClientMenu client={client} />
        </Stack>

        <Divider sx={{mb: 2, mt: 1}} />

        {!client && <Preloader sx={{height: 'calc(100vh - 105px)'}} />}

        {client && <ClientContext.Provider value={client}>
          <Stack gap={3} direction={'row'} mt={2}>
            <Stack gap={2} flex={3}>
              <Stack direction={'row'} width={1} gap={2} alignItems={'center'} justifyContent={'space-between'}>
                <Tabs value={mainTab} sx={{flexGrow: 1, flexShrink: 1 }}>
                  <Tab label="Overview"
                       value="overview"
                       icon={<Layout size={16} weight={'bold'} color="currentColor" />} to="" />
                  <Tab label={<TabLabel label={/*enrollmentsCount*/ ''} color={'success.500'}>Enrollments</TabLabel>}
                       value="enrollments"
                       icon={<CurrencyCircleDollar size={16} weight={'bold'} color="currentColor" />}
                       to="enrollments" />
                  <Tab label={<TabLabel label={nextActions.length}>Next actions</TabLabel>}
                       value="actions"
                       icon={<ListChecks size={16} weight={'bold'} color="currentColor" />}
                       to="actions" />
                  <Tab label={<TabLabel label={callsCount}>Calls</TabLabel>}
                       value="calls"
                       icon={<PhoneCall size={16} weight={'bold'} color="currentColor" />}
                       to="calls" />
                  <Tab label="Emails"
                       value="emails"
                       icon={<Envelope size={16} weight={'bold'} color="currentColor" />}
                       to="emails" />
                  <Tab label={<TabLabel label={client.notes?.length}>Notes</TabLabel>}
                       value="notes"
                       icon={<NotePencil size={16} weight={'bold'} color="currentColor" />}
                       to="notes" />
                  <Tab label="Activity"
                       value="activity"
                       disabled
                       icon={<Pulse size={16} weight={'bold'} color="currentColor" />}
                       to="actions" />
                  <Tab label={<TabLabel>SOA</TabLabel>}
                       value="soa"
                       icon={<UserSound size={16} weight={'bold'} color={soaColor} />}
                       to="soa" />
                </Tabs>
                <SoaStatus />
              </Stack>

              <Outlet />
            </Stack>

            <Divider orientation={'vertical'} />

            <Stack gap={2} flex={1} maxWidth={304} minWidth={304}>
              <Tabs value={sideTab} onChange={(e, value) => setSideTab(value)}>
                <MuiTab label="Personal" iconPosition={'start'} icon={<User size={16} weight={'bold'} color="currentColor" />} />
                <MuiTab label="User Input" iconPosition={'start'} icon={<KeyboardIcon  size={16} weight={'bold'} color="currentColor" />} />
                <MuiTab label="Files" iconPosition={'start'} icon={<FilesIcon size={16} weight={'bold'} color="currentColor" />} />
              </Tabs>

              {sideTab === 0 && <ClientPersonal />}
              {sideTab === 1 && <UserInput />}
              {sideTab === 2 && <Files />}
            </Stack>
          </Stack>
        </ClientContext.Provider>}
      </Box>
    </>
  );
};

const Tab = (props: TabProps & LinkProps) => (
  <MuiTab component={Link} iconPosition={'start'} {...props} />
)
